import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { Home, ArrowLeft } from 'lucide-react';

export default function NotFound() {
  const [glitchActive, setGlitchActive] = useState(false);
  const controls = useAnimation();

  useEffect(() => {
    // Subtle glitch effect every 3 seconds
    const interval = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 200);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden bg-black flex flex-col items-center justify-center px-4">
      {/* Animated gradient background */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(15, 27, 45, 0.8) 0%, rgba(0, 0, 0, 1) 100%)',
        }}
        animate={{
          background: [
            'radial-gradient(circle at 30% 30%, rgba(15, 27, 45, 0.8) 0%, rgba(0, 0, 0, 1) 100%)',
            'radial-gradient(circle at 70% 70%, rgba(255, 106, 0, 0.05) 0%, rgba(0, 0, 0, 1) 100%)',
            'radial-gradient(circle at 30% 30%, rgba(15, 27, 45, 0.8) 0%, rgba(0, 0, 0, 1) 100%)',
          ],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Grid overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255, 106, 0, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 106, 0, 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />

      {/* Main content container */}
      <div className="relative z-10 w-full max-w-4xl mx-auto">
        {/* Glassmorphic card */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 1.2,
            ease: [0.19, 1, 0.22, 1], // Apple's signature easing
          }}
          className="relative backdrop-blur-3xl bg-white/[0.02] rounded-[32px] p-8 sm:p-12 md:p-16 border border-white/[0.05] shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
        >
          {/* Inner glow */}
          <div className="absolute inset-0 rounded-[32px] bg-gradient-to-b from-white/[0.03] to-transparent pointer-events-none" />
          
          {/* Content */}
          <div className="relative text-center">
            {/* Animated hardhat icon - minimal and clean */}
            <motion.div
              className="mb-12 flex justify-center"
              initial={{ opacity: 0, scale: 0.8, rotateY: -180 }}
              animate={{ opacity: 1, scale: 1, rotateY: 0 }}
              transition={{
                duration: 1,
                delay: 0.2,
                ease: [0.19, 1, 0.22, 1],
              }}
            >
              <motion.div
                animate={{
                  y: [0, -8, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative"
              >
                {/* Glow effect */}
                <motion.div
                  className="absolute inset-0 blur-2xl opacity-50"
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.3, 0.5, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <div className="w-24 h-24 bg-[#FF6A00] rounded-full" />
                </motion.div>

                {/* Hardhat icon */}
                <svg
                  width="96"
                  height="96"
                  viewBox="0 0 96 96"
                  fill="none"
                  className="relative"
                >
                  <defs>
                    <linearGradient id="hatGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#FF6A00" />
                      <stop offset="100%" stopColor="#FF8C00" />
                    </linearGradient>
                  </defs>
                  
                  {/* Hardhat main body */}
                  <motion.path
                    d="M48 16C28 16 16 28 16 44V56C16 60 18 64 22.4 64H73.6C78 64 80 60 80 56V44C80 28 68 16 48 16Z"
                    fill="url(#hatGradient)"
                    initial={{ pathLength: 0, fillOpacity: 0 }}
                    animate={{ pathLength: 1, fillOpacity: 1 }}
                    transition={{
                      pathLength: { duration: 1.5, ease: "easeInOut" },
                      fillOpacity: { duration: 0.8, delay: 0.5 },
                    }}
                  />
                  
                  {/* Hardhat brim */}
                  <motion.path
                    d="M16 56L20 68C20 72 24 76 28 76H68C72 76 76 72 76 68L80 56"
                    fill="#FF8C00"
                    initial={{ pathLength: 0, fillOpacity: 0 }}
                    animate={{ pathLength: 1, fillOpacity: 1 }}
                    transition={{
                      pathLength: { duration: 1.5, ease: "easeInOut", delay: 0.2 },
                      fillOpacity: { duration: 0.8, delay: 0.7 },
                    }}
                  />
                  
                  {/* Center reflection */}
                  <motion.circle
                    cx="48"
                    cy="44"
                    r="6"
                    fill="white"
                    fillOpacity="0.9"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 0.9 }}
                    transition={{ duration: 0.6, delay: 1.2, ease: "easeOut" }}
                  />
                </svg>
              </motion.div>
            </motion.div>

            {/* 404 with glitch effect */}
            <motion.div
              className="mb-8 relative"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <h1 
                className={`text-[120px] sm:text-[160px] md:text-[200px] font-black leading-none tracking-tighter transition-all duration-200 ${
                  glitchActive ? 'blur-[2px]' : ''
                }`}
                style={{
                  background: 'linear-gradient(135deg, #FF6A00 0%, #FF8C00 50%, #FFA500 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textShadow: glitchActive 
                    ? '2px 2px 0 rgba(255, 106, 0, 0.5), -2px -2px 0 rgba(255, 140, 0, 0.5)'
                    : 'none',
                }}
              >
                404
              </h1>
              
              {/* Scanning line */}
              <motion.div
                className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#FF6A00] to-transparent"
                animate={{
                  top: ['0%', '100%'],
                  opacity: [0, 1, 1, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  repeatDelay: 2,
                }}
              />
            </motion.div>

            {/* Message */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mb-12 space-y-4"
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-white/95 tracking-tight">
                Page not found
              </h2>
              <p className="text-lg sm:text-xl text-white/50 max-w-md mx-auto font-light leading-relaxed">
                The page you're looking for doesn't exist or has been moved.
              </p>
            </motion.div>

            {/* Action buttons */}
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              {/* Primary button */}
              <Link to="/">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="group relative px-8 py-4 bg-gradient-to-b from-[#FF6A00] to-[#FF5500] text-white rounded-2xl font-semibold text-base shadow-[0_1px_40px_rgba(255,106,0,0.3)] hover:shadow-[0_1px_50px_rgba(255,106,0,0.5)] transition-all duration-500 flex items-center gap-3 overflow-hidden"
                >
                  {/* Shine effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%', skewX: -20 }}
                    whileHover={{ x: '200%' }}
                    transition={{ duration: 0.8 }}
                  />
                  
                  <Home size={20} strokeWidth={2} className="relative z-10" />
                  <span className="relative z-10">Return home</span>
                </motion.button>
              </Link>

              {/* Secondary button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => window.history.back()}
                className="px-8 py-4 bg-white/[0.05] hover:bg-white/[0.08] backdrop-blur-xl text-white/80 hover:text-white rounded-2xl font-semibold text-base border border-white/[0.08] hover:border-white/[0.12] transition-all duration-300 flex items-center gap-3"
              >
                <ArrowLeft size={20} strokeWidth={2} />
                <span>Go back</span>
              </motion.button>
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom accent line */}
        <motion.div
          className="mt-8 mx-auto w-32 h-[1px] bg-gradient-to-r from-transparent via-[#FF6A00]/50 to-transparent"
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 1.5, delay: 1 }}
        />
      </div>

      {/* Ambient light effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#FF6A00]/5 rounded-full blur-[100px] pointer-events-none" />
    </div>
  );
}
